import React, { useEffect, useState } from 'react';

import { CrossIcon } from '@vlabs/icons';
import { Control } from '@vlabs/uikit';
import cn from 'classnames';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import st from './FullscreenImageViewer.module.sass';

interface PhotoViewerProps {
  open: boolean;
  onClose: VoidFunction;
  url?: string;
  className?: string;
}

type TPhotoViewerStatus = 'idle' | 'loading' | 'success' | 'error';

export function FullscreenImageViewer({
  open,
  url = undefined,
  className = undefined,
  onClose,
}: PhotoViewerProps): React.JSX.Element | null {
  const { t } = useTranslation();
  const [status, setStatus] = useState<TPhotoViewerStatus>('idle');

  useEffect(() => {
    if (!open) {
      setStatus('idle');
      return undefined;
    }

    if (!url) {
      setStatus('error');
      return undefined;
    }

    setStatus('loading');

    const image = new Image();

    image.onload = () => {
      setStatus('success');
    };

    image.onerror = () => {
      setStatus('error');
    };

    image.src = url;

    return () => {
      image.onload = null;
      image.onerror = null;
    };
  }, [open, url]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return createPortal(
    <div
      aria-modal="true"
      className={cn(st.Root, className)}
      role="dialog"
    >
      <Control.IconButton
        className={st.CloseButton}
        icon={<CrossIcon />}
        kind="secondary"
        onClick={onClose}
      />

      <div className={st.Content}>
        {status === 'loading' && (
          <div className={st.State}>
            {t('monokit:fullscreen_image_viewer.status.loading')}
          </div>
        )}

        {status === 'error' && (
          <div className={st.State}>
            {t('monokit:fullscreen_image_viewer.status.error')}
          </div>
        )}

        {status === 'success' && (
          <img
            alt="fullscreenImage"
            className={st.Image}
            src={url}
          />
        )}
      </div>
    </div>,
    document.body,
  );
}
